import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { SITE } from "@/constants/testIds";

const sources = [
  {
    title: "White House Jewish American Heritage Month, 2026 Proclamation",
    body: "Each year, Jewish American Heritage Month honors the enduring contributions of Jewish Americans to the life, culture, and conscience of the United States. The 2026 proclamation arrives alongside America's 250th anniversary — a moment to recognize the families, faith communities, and traditions woven into the nation's story.",
  },
  {
    title: "Touro Synagogue & George Washington's 1790 Newport Letter",
    body: "In August 1790, President George Washington wrote to the Hebrew Congregation at Newport, Rhode Island, affirming that the United States would give \"to bigotry no sanction, to persecution no assistance.\" Touro Synagogue remains the oldest synagogue building in North America and stands as a living reminder of America's earliest promise of religious freedom.",
  },
  {
    title: "Senate Bipartisan Task Force for Combating Antisemitism",
    body: "Established as a bipartisan effort to address the rise of antisemitism in the United States, the task force reflects a shared commitment — across political lines — to protect Jewish communities and the broader principle of religious liberty.",
  },
  {
    title: "Ner (נר) = 250",
    body: "In Hebrew, the word for candle is נר (Ner). Its letters carry numerical value: Nun (נ) = 50 and Resh (ר) = 200 — together, 250. The same number America reaches in 2026. The candle is the quiet symbol that ties the song to this anniversary: light, memory, and the responsibility to carry both forward.",
  },
];

export default function SourceNotes() {
  useEffect(() => {
    document.title =
      "Source Notes & Inspiration — Shabbat Shalom, America";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid={SITE.sourceNotesPage}>
      <Navbar />
      <main className="pt-32 pb-24 bg-background bg-grain min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <Link
            to="/"
            data-testid={SITE.sourceNotesBack}
            className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors duration-300 mb-12"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to the song
          </Link>

          <p className="text-xs uppercase tracking-[0.32em] text-primary/90 mb-5">
            Source Notes &amp; Inspiration
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05] tracking-tight text-balance">
            Where the light
            <br />
            <span className="italic text-primary">comes from</span>.
          </h1>
          <div className="gold-rule w-24 mt-8 mb-14" />

          <div className="space-y-14">
            {sources.map((s, i) => (
              <article key={i}>
                <p className="text-xs uppercase tracking-[0.28em] text-primary/80 mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h2 className="font-serif text-2xl sm:text-3xl text-foreground leading-tight tracking-tight">
                  {s.title}
                </h2>
                <p className="mt-4 text-foreground/75 text-base leading-relaxed">
                  {s.body}
                </p>
                <div className="mt-8 h-px w-10 bg-primary/50" />
              </article>
            ))}
          </div>

          <div className="mt-20 border border-border/60 bg-card/40 backdrop-blur-sm p-8 sm:p-10">
            <p className="text-[11px] uppercase tracking-[0.28em] text-primary/90 mb-4">
              Disclaimer
            </p>
            <p className="text-foreground/75 text-sm sm:text-base leading-relaxed">
              Sources are provided for historical context, inspiration,
              gratitude, and transparency only. Inclusion does not imply
              endorsement, sponsorship, or political affiliation. Shabbat
              Shalom, America is a cultural song project — not an
              organizational or political effort.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
